import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { $getSelection, $isRangeSelection, $setSelection, KEY_DOWN_COMMAND, COMMAND_PRIORITY_LOW } from 'lexical';
import { mergeRegister } from '@lexical/utils';
import { useCallback, useEffect, useRef, useState } from 'react';
import { $getTableCellNodeFromLexicalNode } from '@lexical/table';

import type { JSX } from 'react';
import type { Actions } from 'components/RteEditor/Plugins/TablePlugin/TableCellContextMenu/TableCellContextMenu';
import type { TableMutation } from 'components/RteEditor/Plugins/TablePlugin/TablePlugin';
import type { SearchResponse } from 'components/RteEditor/Plugins/CourseCataloguePlugin/AddCatalogueLinkForm';

import { CourseCataloguePlugin } from 'components/RteEditor/Plugins/CourseCataloguePlugin';
import {
  LinkPlugin,
  BlockFormatPlugin,
  ListPlugin,
  HistoryPlugin,
  TablePlugin,
  TableCellContextMenu,
} from 'components/RteEditor/Plugins';
import styles from 'components/RteEditor/RteEditor.module.css';
import { removeScrollableContainers, setScrollableContainers } from 'components/RteEditor/helpers';

export type Position = {
  xPos: number;
  yPos: number;
  height: number;
};

interface ToolBarProps {
  editorScrollPosition: number;
  onCatalogueLinkSearch?: (query: string, linkType: 'course' | 'programme') => void;
  catalogueSearchResponse?: SearchResponse;
}

export const ToolBarPlugin = ({
  editorScrollPosition,
  onCatalogueLinkSearch,
  catalogueSearchResponse,
}: ToolBarProps): JSX.Element => {
  const [editor] = useLexicalComposerContext();
  const [position, setPosition] = useState<Position | null>(null);
  const tableref = useRef<TableMutation>(null);

  const updateToolbar = useCallback(() => {
    const selection = $getSelection();
    if (!$isRangeSelection(selection)) {
      setPosition(null);
      return;
    }

    const anchorNode = selection.anchor.getNode();
    const tableCell = $getTableCellNodeFromLexicalNode(anchorNode);
    if (!tableCell) {
      setPosition(null);
      return;
    }

    const domNode = editor.getElementByKey(tableCell.getKey());
    if (!domNode) return;

    const rect = domNode.getBoundingClientRect();
    setPosition({
      xPos: rect.right,
      yPos: rect.bottom,
      height: rect.height,
    });
  }, [editor]);

  const handleTableAction = (action: Actions) => {
    tableref.current?.mutateTable(action);
  };

  const handleResizeScroll = useCallback(() => {
    editor.update(() => {
      const selection = $getSelection();

      if ($isRangeSelection(selection)) {
        const anchor = selection.anchor;
        const anchorNode = anchor.getNode();

        const tableCell = $getTableCellNodeFromLexicalNode(anchorNode);
        if (tableCell) {
          $setSelection(null);
          setPosition(null);
        }
      }
    });
  }, [editor]);

  useEffect(() => {
    return mergeRegister(
      editor.registerUpdateListener(({ editorState }) => {
        editorState.read(updateToolbar);
      }),

      editor.registerCommand(
        KEY_DOWN_COMMAND,
        () => {
          setScrollableContainers(handleResizeScroll);
          return false;
        },
        COMMAND_PRIORITY_LOW
      ),

      editor.registerRootListener((rootElement, prevRootElement) => {
        if (prevRootElement) {
          removeScrollableContainers(handleResizeScroll);
        }

        if (rootElement) {
          setScrollableContainers(handleResizeScroll);
        }
      })
    );
  }, [editor, updateToolbar, handleResizeScroll]);

  return (
    <div className={styles['editor__toolbar']}>
      <BlockFormatPlugin />
      <ListPlugin />
      <TablePlugin isActive={Boolean(position)} ref={tableref} />
      {position && (
        <TableCellContextMenu
          position={position}
          parentScrollPosition={editorScrollPosition}
          onAction={handleTableAction}
        />
      )}
      <LinkPlugin parentScrollPosition={editorScrollPosition} />
      {onCatalogueLinkSearch && catalogueSearchResponse && (
        <CourseCataloguePlugin
          parentScrollPosition={editorScrollPosition}
          onSearch={onCatalogueLinkSearch}
          searchResponse={catalogueSearchResponse}
        />
      )}
      <HistoryPlugin />
    </div>
  );
};
